import { useDisclosure, useLocalStorage } from "@mantine/hooks"
import { Plus, X } from "@phosphor-icons/react"
import { Button, Tag, Toast } from "@vesatogo/grass-core"
import toast from "react-hot-toast"
import { FormCard } from "~/components/FormCard"
import { TradeKinds } from "~/constants/Kinds"
import {
  useAuthenticatedUserQuery,
  useTradeSelectorQuery,
} from "~/generated/graphql"
import SelectPattiModal from "./SelectPattiModal"

export const OrderPattiRefs = ({ form, inputProps }: any) => {
  const {
    value: selectedPattis,
    onChange,
    disabled,
  } = inputProps("order_trade_ref_ids")
  const [isOpen, { open, close }] = useDisclosure(false)
  const [{ data: aData }] = useAuthenticatedUserQuery()

  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })

  const commodityIds = form.values?.output_items
    ?.map(it => it?.commodity?.shared_id)
    ?.filter(a => a)
  const varietyIds = form.values?.output_items
    ?.map(it => it?.variety?.shared_id)
    ?.filter(a => a)

  const [{ data, fetching: isLoading }] = useTradeSelectorQuery({
    variables: {
      where: {
        kind: { _eq: TradeKinds.OrderPatti },
        current_status_id: {
          _eq: "Verified",
        },
        trade_items: {
          commodity_id: {
            _in: commodityIds,
          },
          commodity_variety_id: {
            _in: varietyIds,
          },
        },

        department: {
          reference_id: {
            _eq: (department && String(department)) || currentDeptCodename,
          },
        },
      },
    },
    pause: !isOpen || !commodityIds?.length || !varietyIds?.length,
    requestPolicy: "network-only",
  })

  const handleSelect = (selected: any[]) => {
    const newItems = selected.map(s => ({
      id: s.id,
      name: s.id, // Or another identifier if needed
    }))

    // Merge with existing ones, avoiding duplicates
    const combined = [...selectedPattis]
    newItems.forEach(item => {
      if (!combined.find(c => c.id === item.id)) {
        combined.push(item)
      }
    })

    onChange(combined)
  }

  const handleRemove = (id: string) => {
    onChange(selectedPattis.filter((p: any) => p.id !== id))
  }

  return (
    <FormCard
      title={
        <div className="flex items-center gap-2">
          <span className="text-green-400">Order Patti Ref's</span>
          <span className="text-xs text-blue-400 !font-400">
            (Note: Order Patti is optional and supports multi-selection. It will
            be used only as a reference.)
          </span>
        </div>
      }
    >
      <div className="flex flex-wrap gap-2 items-center min-h-[40px]  border border-gray-200 rounded p-2 bg-gray-50">
        {selectedPattis?.map((patti: any) => (
          <Tag key={patti.id} intent="info" disabled={disabled}>
            <div className="flex gap-1 items-center">
              #{patti.name}
              <X
                size={12}
                className="cursor-pointer hover:text-gray-200"
                onClick={() => handleRemove(patti.id)}
              />
            </div>
          </Tag>
        ))}
        <Button
          variant="minimal"
          size="sm"
          leftIcon={<Plus />}
          onClick={() => {
            if (!commodityIds?.length || !varietyIds?.length) {
              return toast.custom(
                <Toast
                  intent="danger"
                  title="Please select Output commodity and Variety first"
                />
              )
            }
            open()
          }}
          className="text-blue-500"
          disabled={disabled}
        >
          Add Patti
        </Button>
      </div>

      <SelectPattiModal
        isOpen={isOpen}
        onClose={close}
        onSelect={handleSelect}
        data={data?.trade || []}
        isLoadingData={isLoading}
        type="order"
      />
    </FormCard>
  )
}
